<template>
  <main class="math-page" :class="appStore.theme" :lang="appStore.language">
    <header>
      <div><p class="eyebrow">{{ t('math_rush.challenge') }}</p><h1>{{ gameName }}</h1></div>
      <div class="progress-wrap">
        <span>{{ t('math_rush.round_progress', { current: roundIndex + 1, total: rounds.length }) }}</span>
        <div class="progress"><i :style="{ width: `${((roundIndex + 1) / rounds.length) * 100}%` }"></i></div>
      </div>
      <div class="timer" :class="{ urgent: phase === 'answering' && secondsLeft <= 3, review: phase === 'reveal', inactive: !['answering', 'reveal'].includes(phase) }">{{ ['answering', 'reveal'].includes(phase) ? secondsLeft : '' }}</div>
    </header>

    <section class="round-stage">
      <div class="calculation-card">
        <span>{{ t('math_rush.solve') }}</span>
        <strong>{{ currentRound.expression }}</strong>
      </div>
      <p v-if="phase === 'answering'" class="round-status">{{ t('math_rush.answers_received', { answered: answerCount, total: activePlayers.length }) }}</p>
      <div v-else-if="phase === 'reveal'" class="reveal-card">
        <p>{{ t('math_rush.correct_answer') }}</p>
        <strong>{{ currentRound.answer }}</strong>
        <p>{{ roundResultText }}</p>
      </div>
    </section>

    <aside class="scoreboard">
      <h3>{{ t('math_rush.scoreboard') }}</h3>
      <div v-for="(player, index) in rankedPlayers" :key="player.id" class="player-row" :style="{ '--player-colour': player.playerColour || '#58b8ff' }">
        <span class="rank">{{ index + 1 }}</span>
        <div class="avatar-wrap">
          <img v-if="playerImage(player)" :src="playerImage(player)" class="avatar" alt="" />
          <span v-else class="avatar initials">{{ initials(playerName(player)) }}</span>
          <i class="connection-dot" :class="{ offline: player.connected === false, away: player.connected !== false && !player.inGame }" :style="{ backgroundColor: player.connected === false ? '#ff4d5e' : player.inGame === true ? '#40dc74' : '#ffad32' }"></i>
        </div>
        <div class="player-details">
          <strong>{{ playerName(player) }}</strong>
          <small v-if="phase === 'reveal' && hasAnswer(player.id)" :class="{ correct: isCorrect(player.id), wrong: !isCorrect(player.id) }">
            {{ t('math_rush.answer_result', { answer: answersByPlayer[player.id], points: roundPoints[player.id] || 0 }) }}
          </small>
          <small v-else-if="phase === 'reveal'">{{ t('math_rush.no_answer') }}</small>
          <small v-else>{{ hasAnswer(player.id) ? t('math_rush.submitted') : t('math_rush.thinking') }}</small>
        </div>
        <div class="player-score"><b>{{ scores[player.id] || 0 }}</b><small>{{ t('math_rush.points') }}</small></div>
      </div>
    </aside>

    <RoundCountdown v-if="phase === 'intermission'" :count="intermissionCount" />
    <section v-if="phase === 'finished'" class="result-overlay">
      <div class="fireworks"><i v-for="spark in 48" :key="spark" :style="confettiStyle(spark)"></i></div>
      <div class="result-card">
        <div class="trophy">&#9733;</div>
        <h2>{{ winners.length === 1 ? t('math_rush.player_wins', { player: playerName(winners[0]) }) : t('math_rush.players_tie', { players: winners.map(playerName).join(', ') }) }}</h2>
        <ol><li v-for="player in rankedPlayers" :key="player.id"><span>{{ playerName(player) }}</span><b>{{ scores[player.id] || 0 }}</b></li></ol>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import eventBus from '@/plugins/eventBus'
import { useAppStore } from '@/stores/appStore'
import { useWebsocketStore } from '@/stores/websocketStore'
import RoundCountdown from '@/components/RoundCountdown.vue'
import { soundManager, SOUNDS } from '@/services/soundManager'

const TOTAL_ROUNDS = 10
const ROUND_SECONDS = 10
const REVIEW_SECONDS = 5
const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t } = useI18n()
const game = computed(() => appStore.room?.game || appStore.game || {})
const players = computed(() => appStore.room?.game?.players || [])
// Do not reject a valid answer because the diagnostic presence flag has not
// caught up with the player's controller screen yet.
const activePlayers = computed(() => players.value.filter(player => player.connected !== false))
const rounds = ref(generateRounds())
const roundIndex = ref(0)
const phase = ref('answering')
const secondsLeft = ref(ROUND_SECONDS)
const intermissionCount = ref(3)
const answersByPlayer = reactive({})
const roundPoints = reactive({})
const scores = reactive({})
const acceptedRequestIds = new Set()
let timer
let nextRoundTimer
let reviewFinishTimer

const currentRound = computed(() => rounds.value[roundIndex.value] || { expression: '', answer: 0 })
const gameName = computed(() => {
  const name = game.value.name
  return typeof name === 'object' ? (name.en || Object.values(name)[0] || 'Math Rush') : (name || 'Math Rush')
})
const answerCount = computed(() => {
  const activeIds = new Set(activePlayers.value.map(player => String(player.id)))
  return Object.keys(answersByPlayer).filter(playerId => activeIds.has(String(playerId))).length
})
const rankedPlayers = computed(() => [...players.value].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)))
const topScore = computed(() => rankedPlayers.value.length ? scores[rankedPlayers.value[0].id] || 0 : 0)
const winners = computed(() => rankedPlayers.value.filter(player => (scores[player.id] || 0) === topScore.value))
const correctPlayers = computed(() => activePlayers.value.filter(player => isCorrect(player.id)))
const roundResultText = computed(() => correctPlayers.value.length
  ? t('math_rush.correct_players', { players: correctPlayers.value.map(playerName).join(', ') })
  : t('math_rush.no_correct_answers'))

function integer(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min }
function generateRound(index) {
  let a, b, c, d, answer
  if (index === 0) { a = integer(1, 10); b = integer(1, 10); return { expression: `${a} + ${b}`, answer: a + b, difficulty: 1 } }
  if (index === 1) { a = integer(5, 20); b = integer(1, a); return { expression: `${a} - ${b}`, answer: a - b, difficulty: 1 } }
  if (index === 2) { a = integer(10, 50); b = integer(10, 50); return { expression: `${a} + ${b}`, answer: a + b, difficulty: 2 } }
  if (index === 3) { a = integer(30, 99); b = integer(10, a); return { expression: `${a} - ${b}`, answer: a - b, difficulty: 2 } }
  if (index === 4) { a = integer(2, 9); b = integer(2, 9); return { expression: `${a} x ${b}`, answer: a * b, difficulty: 2 } }
  if (index === 5) { b = integer(2, 9); answer = integer(2, 12); a = b * answer; return { expression: `${a} / ${b}`, answer, difficulty: 2 } }
  if (index === 6) { a = integer(3, 12); b = integer(3, 12); c = integer(2, 20); return { expression: `${a} x ${b} + ${c}`, answer: a * b + c, difficulty: 3 } }
  if (index === 7) { a = integer(10, 40); b = integer(2, 9); c = integer(2, 9); return { expression: `${a} + ${b} x ${c}`, answer: a + b * c, difficulty: 3 } }
  if (index === 8) { a = integer(5, 20); b = integer(2, 15); c = integer(2, 6); return { expression: `(${a} + ${b}) x ${c}`, answer: (a + b) * c, difficulty: 3 } }
  a = integer(5, 15); b = integer(4, 12); c = integer(2, 8); d = integer(2, 8)
  const left = a * b; const right = c * d
  return left >= right
    ? { expression: `${a} x ${b} - ${c} x ${d}`, answer: left - right, difficulty: 4 }
    : { expression: `${c} x ${d} - ${a} x ${b}`, answer: right - left, difficulty: 4 }
}
function generateRounds() { return Array.from({ length: TOTAL_ROUNDS }, (_, index) => generateRound(index)) }
function playerName(player) { return player?.displayName || player?.name || player?.email || t('math_rush.player') }
function playerImage(player) { return player?.avatar || player?.photoURL || player?.image || '' }
function initials(name) { return String(name).trim().split(/\s+/).map(word => word[0]).join('').slice(0, 2).toUpperCase() }
function hasAnswer(playerId) { return Object.prototype.hasOwnProperty.call(answersByPlayer, playerId) }
function isCorrect(playerId) { return hasAnswer(playerId) && answersByPlayer[playerId] === currentRound.value.answer }
function clearAnswers() { Object.keys(answersByPlayer).forEach(key => delete answersByPlayer[key]); Object.keys(roundPoints).forEach(key => delete roundPoints[key]) }
function startRound() {
  clearInterval(timer); clearInterval(nextRoundTimer); clearTimeout(reviewFinishTimer); clearAnswers()
  secondsLeft.value = ROUND_SECONDS; phase.value = 'answering'
  soundManager.stopAll(); soundManager.play(SOUNDS.roundStart); soundManager.schedule(SOUNDS.quizCountdown, 450, { exclusiveCategory: true })
  timer = setInterval(() => { secondsLeft.value -= 1; if (secondsLeft.value <= 0) revealRound(true) }, 1000)
}
function handleControl(control) {
  if (control?.requestId && acceptedRequestIds.has(control.requestId)) {
    acknowledgeControl(control, true)
    return
  }
  if (phase.value !== 'answering' || !['number-input', 'number', 'estimate'].includes(control?.type)) {
    acknowledgeControl(control, false)
    return
  }
  const player = activePlayers.value.find(item => item.id === control.userId)
  const value = Number(control.value)
  if (!player || hasAnswer(player.id) || !Number.isInteger(value)) {
    acknowledgeControl(control, false)
    return
  }
  answersByPlayer[player.id] = value
  const difficultyBonus = Number(currentRound.value.difficulty || 1) * 25
  const points = value === currentRound.value.answer ? 75 + difficultyBonus + secondsLeft.value * 15 : 0
  roundPoints[player.id] = points
  scores[player.id] = (scores[player.id] || 0) + points
  if (control.requestId) acceptedRequestIds.add(control.requestId)
  acknowledgeControl(control, true)
  const submittedIds = new Set(Object.keys(answersByPlayer).map(String))
  const everybodyAnswered = activePlayers.value.length > 0
    && activePlayers.value.every(player => submittedIds.has(String(player.id)))
  if (everybodyAnswered) revealRound(false)
}
function acknowledgeControl(control, accepted) {
  if (!control?.requestId || !control?.userId) return
  socketStore.send({ event: 'control-result', data: { requestId: control.requestId, userId: control.userId, accepted } })
}
function revealRound(timedOut = false) {
  if (phase.value !== 'answering') return
  clearInterval(timer); clearInterval(nextRoundTimer); clearTimeout(reviewFinishTimer); soundManager.stopCategory('countdown'); const hasCorrect = correctPlayers.value.length > 0; soundManager.play(timedOut ? SOUNDS.timesUp : (hasCorrect ? SOUNDS.correct : SOUNDS.wrong)); if (hasCorrect) soundManager.schedule(SOUNDS.points, 650)
  phase.value = 'reveal'; secondsLeft.value = REVIEW_SECONDS
  activePlayers.value.forEach(player => { if (!(player.id in roundPoints)) roundPoints[player.id] = 0 })
  nextRoundTimer = setInterval(() => {
    secondsLeft.value = Math.max(0, secondsLeft.value - 1)
  }, 1000)
  // Keep result presentation independent from the visual countdown. This
  // guarantees that round 10 receives the same full review time as all other
  // rounds before the final scoreboard covers it.
  reviewFinishTimer = setTimeout(() => {
    clearInterval(nextRoundTimer)
    if (roundIndex.value + 1 >= rounds.value.length) finishGame()
    else startIntermission()
  }, REVIEW_SECONDS * 1000)
}
function startIntermission() {
  soundManager.play(SOUNDS.nextRoundCountdown, { exclusiveCategory: true })
  clearTimeout(reviewFinishTimer)
  phase.value = 'intermission'; intermissionCount.value = 3
  nextRoundTimer = setInterval(() => {
    intermissionCount.value -= 1
    if (intermissionCount.value > 0) return
    clearInterval(nextRoundTimer)
    roundIndex.value += 1
    startRound()
  }, 1000)
}
function finishGame() {
  soundManager.stopAll(); soundManager.play(SOUNDS.winner)
  phase.value = 'finished'
  socketStore.send({ event: 'finished', data: { gameId: appStore.game?.id, scores: { ...scores }, winnerIds: winners.value.map(player => player.id), rounds: rounds.value } })
}
function restartGame() {
  clearInterval(timer); clearInterval(nextRoundTimer); clearTimeout(reviewFinishTimer); roundIndex.value = 0; rounds.value = generateRounds()
  Object.keys(scores).forEach(key => { scores[key] = 0 }); startRound()
}
function confettiStyle(spark) { return { '--x': `${(spark * 37) % 100}%`, '--delay': `${(spark % 12) * -.18}s`, '--drift': `${((spark * 29) % 180) - 90}px`, '--colour': `hsl(${(spark * 43) % 360},90%,62%)` } }

onMounted(() => {
  players.value.forEach(player => { scores[player.id] = 0 })
  eventBus.on('game-control', handleControl); eventBus.on('restart-round', restartGame)
  socketStore.send({ event: 'playing', data: { gameId: game.value.id } }); startRound()
})
onBeforeUnmount(() => {
  clearInterval(timer); clearInterval(nextRoundTimer); clearTimeout(reviewFinishTimer); soundManager.stopAll()
  eventBus.off('game-control', handleControl); eventBus.off('restart-round', restartGame)
})
</script>

<style scoped>
.math-page{height:100vh;box-sizing:border-box;overflow:hidden;padding:2vh 2vw;display:grid;grid-template-columns:minmax(0,1fr) minmax(250px,28vw);grid-template-rows:clamp(62px,10vh,88px) minmax(0,1fr);gap:1.5vh 1.5vw;color:#fff;background:radial-gradient(circle at 25% 15%,#174c5a,#101f2a 42%,#070d13)}.math-page.light{color:#172033;background:#edf7f7}header{grid-column:1/-1;height:100%;display:flex;align-items:center;gap:1.5vw}header h1,.eyebrow{margin:0}header h1{font-size:clamp(1.3rem,3.2vh,2.25rem)}.eyebrow{text-transform:uppercase;letter-spacing:.1em;color:#6ee7d8;font-size:clamp(.7rem,1.6vh,.95rem);font-weight:800}.progress-wrap{margin-left:auto;width:min(30vw,380px);font-size:clamp(.72rem,1.7vh,1rem);font-weight:800}.progress{height:7px;margin-top:.5vh;overflow:hidden;border-radius:20px;background:#ffffff25}.progress i{display:block;height:100%;background:#55e0c5}.timer{flex:0 0 clamp(3rem,7vh,4.4rem);width:clamp(3rem,7vh,4.4rem);height:clamp(3rem,7vh,4.4rem);box-sizing:border-box;display:grid;place-items:center;border:4px solid #55e0c5;border-radius:999px;background:#163b45;font-size:clamp(1.2rem,3.2vh,2rem);font-weight:900}.timer.inactive{visibility:hidden}.timer.urgent{border-color:#ff6575;color:#ff8f9b}.round-stage{min-width:0;display:flex;flex-direction:column;gap:1.5vh}.calculation-card,.reveal-card{border:1px solid #ffffff20;border-radius:22px;background:#ffffff0d;text-align:center}.calculation-card{min-height:38vh;display:grid;place-content:center;padding:3vh 3vw}.calculation-card span,.reveal-card>p:first-child{text-transform:uppercase;letter-spacing:.1em;opacity:.7;font-weight:800}.calculation-card strong{display:block;margin-top:2vh;font-size:clamp(3rem,11vh,8rem);line-height:1}.round-status{text-align:center;font-size:clamp(.8rem,2vh,1.2rem);font-weight:800}.reveal-card{padding:2.5vh 2vw}.reveal-card p{margin:.5vh}.reveal-card strong{display:block;color:#72f1d8;font-size:clamp(2.5rem,7vh,5rem)}.scoreboard{min-width:0;padding:1.5vh 1vw;overflow:hidden;border:1px solid #ffffff18;border-radius:20px;background:#ffffff0b}.scoreboard h3{margin:0 0 1vh;font-size:clamp(.9rem,2.1vh,1.2rem)}.player-row{display:grid;grid-template-columns:auto auto minmax(0,1fr) auto;align-items:center;gap:.55vw;padding:.8vh .35vw;border-left:4px solid var(--player-colour);border-bottom:1px solid #ffffff16}.rank{font-weight:900;color:#72f1d8}.avatar-wrap{position:relative}.avatar{width:clamp(1.7rem,4.5vh,2.7rem);height:clamp(1.7rem,4.5vh,2.7rem);border-radius:50%;object-fit:cover}.initials{display:grid;place-items:center;background:#285c68;font-weight:900}.connection-dot{position:absolute;right:-2px;bottom:1px;width:.7rem;height:.7rem;border:2px solid #17323a;border-radius:50%;background:#40dc74}.connection-dot.away{background:#ffad32}.connection-dot.offline{background:#ff4d5e}.player-details{min-width:0;display:flex;flex-direction:column}.player-details strong,.player-details small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.player-details strong{font-size:clamp(.72rem,1.7vh,1rem)}.player-details small{font-size:clamp(.62rem,1.45vh,.82rem);opacity:.75}.player-details small.correct{color:#69efa5;opacity:1}.player-details small.wrong{color:#ff8791;opacity:1}.player-score{min-width:3rem;padding:.4rem .5rem;border-radius:.7rem;background:#55e0c520;text-align:center}.player-score b{display:block;font-size:clamp(1rem,2.6vh,1.5rem);line-height:1}.player-score small{display:block;font-size:.62rem;text-transform:uppercase;opacity:.7}.result-overlay{position:fixed;inset:0;z-index:50;display:grid;place-items:center;background:#051014e8}.result-card{position:relative;z-index:2;width:min(75vw,650px);padding:4vh 4vw;border-radius:28px;background:#fff;color:#172033;text-align:center}.trophy{font-size:clamp(4rem,12vh,8rem);color:#24bfa4}.result-card h2{margin:0 0 2vh;font-size:clamp(2rem,5vh,3.7rem)}.result-card ol{max-width:450px;margin:auto;padding:0;list-style:none}.result-card li{display:flex;justify-content:space-between;padding:1vh;border-bottom:1px solid #dcebea}.fireworks{position:absolute;inset:0;overflow:hidden}.fireworks i{position:absolute;left:var(--x);top:-5vh;width:.65rem;height:1rem;background:var(--colour);animation:fall 3s linear infinite;animation-delay:var(--delay)}.light .calculation-card,.light .reveal-card,.light .scoreboard{border-color:#cfe1e2;background:#fff}.light .timer{background:#fff}@keyframes fall{to{transform:translateX(var(--drift)) translateY(115vh) rotate(780deg);opacity:.15}}
.timer.review{border-color:#8db9ff;color:#8db9ff}
</style>
